'use client';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import ImageCompareSlider from './ImageCompareSlider';
import SignUpModule from './SignUpModule';
import dynamic from 'next/dynamic';

const VideoJS = dynamic(() => import('./VideoJs'), {
  ssr: false,
  loading: () => <h1>Loading...</h1>,
});

const processImageUrl = (url: string) => {
  const [baseUrl, query] = url.split('?');
  const urlParams = new URLSearchParams(query);
  const width = urlParams.get('width');
  const height = urlParams.get('height');

  return { baseUrl, width, height };
};

const parseStyle = (styleString: string) => {
  return styleString.split(';').reduce(
    (styleObject, styleProperty) => {
      const [property, value] = styleProperty.split(':');
      if (property && value) {
        styleObject[property.trim()] = value.trim();
      }
      return styleObject;
    },
    {} as { [key: string]: string }
  );
};

const VideoObserver = ({
  sources,
  width,
  height,
}: {
  sources: any;
  width?: string;
  height?: string;
}) => {
  const options = {
    autoplay: true,
    muted: true,
    loop: true,
    controls: true,
    playsinline: true,
    responsive: true,
    fluid: true,
    sources,
  };

  return (
    <VideoJS
      options={options}
      onReady={(player: any) => {
        player
          .play()
          .catch((error: any) => console.error('Error playing video:', error));
      }}
      width={width}
      height={height}
    />
  );
};

const ContentClientEnhancer = ({ content }: { content: string }) => {
  useEffect(() => {
    const roots: any[] = [];
    const contentRoot = document.getElementById('article-content');
    if (!contentRoot) {
      return;
    }

    const images = contentRoot.querySelectorAll('img');
    images.forEach((img) => {
      if (img.closest('p.compare')) {
        return;
      }

      const src = img.getAttribute('src');
      const alt = img.getAttribute('alt');
      const className = img.getAttribute('class');
      const isToggle = className?.includes('toggle');
      const isWebpVideo = className?.includes('webp');
      const { baseUrl, width, height } = processImageUrl(src ?? '');
      const style = img.getAttribute('style');

      const zoomComponent = isToggle ? (
        <Zoom
          zoomImg={{
            src: (isWebpVideo ? baseUrl : src) ?? '',
          }}
        >
          <img
            src={(isWebpVideo ? baseUrl : src) ?? ''}
            alt={alt ?? ''}
            className={className ?? ''}
            style={{
              borderRadius: '8px',
              width: isWebpVideo && width ? `${width}px` : '',
              height: isWebpVideo && height ? `${height}px` : '',
              objectFit: isWebpVideo ? 'cover' : 'contain',
              ...(style ? parseStyle(style) : {}),
            }}
          />
        </Zoom>
      ) : (
        <img
          src={(isWebpVideo ? baseUrl : src) ?? ''}
          alt={alt ?? ''}
          className={className ?? ''}
          style={{
            borderRadius: '8px',
            width: isWebpVideo && width ? `${width}px` : '',
            height: isWebpVideo && height ? `${height}px` : '',
            objectFit: isWebpVideo ? 'cover' : 'contain',
            ...(style ? parseStyle(style) : {}),
          }}
        />
      );

      const container = document.createElement('div');
      img.replaceWith(container);
      const root = createRoot(container);
      root.render(zoomComponent);
      roots.push(root);
    });

    const paragraphs = contentRoot.querySelectorAll('p.compare');
    paragraphs.forEach((p) => {
      const imgElements = p.querySelectorAll('img');
      if (imgElements.length === 2) {
        const img1Src = imgElements[0].getAttribute('src') ?? '';
        const img2Src = imgElements[1].getAttribute('src') ?? '';

        const imageCompareSliderComponent = (
          <ImageCompareSlider img1={img1Src} img2={img2Src} />
        );

        const container = document.createElement('div');
        p.replaceWith(container);
        const root = createRoot(container);
        root.render(imageCompareSliderComponent);
        roots.push(root);
      }
    });

    const videos = contentRoot.querySelectorAll('video');
    videos.forEach((video) => {
      const width = video.getAttribute('width');
      const height = video.getAttribute('height');

      const sources = Array.from(video.querySelectorAll('source')).map(
        (source) => ({
          src: source.getAttribute('src'),
          type: source.getAttribute('type'),
        })
      );
      const container = document.createElement('div');
      video.replaceWith(container);
      const root = createRoot(container);
      root.render(
        <VideoObserver
          sources={sources}
          width={width ?? ''}
          height={height ?? ''}
        />
      );
      roots.push(root);
    });

    const btns = contentRoot.querySelectorAll('a.article-btn');
    btns.forEach((btn) => {
      btn.parentElement?.classList.add('article-btn-wrapper');
    });

    const placeholder = document.getElementById('signup-module');
    if (placeholder) {
      const root = createRoot(placeholder);
      root.render(<SignUpModule />);
      roots.push(root);
    }

    return () => {
      roots.forEach((root) => {
        try {
          root.unmount();
        } catch (e) {
          console.error('Error unmounting root:', e);
        }
      });
    };
  }, [content]);

  useEffect(() => {
    const instagramScript = document.createElement('script');
    instagramScript.src = 'https://www.instagram.com/embed.js';
    instagramScript.async = true;

    const tiktokScript = document.createElement('script');
    tiktokScript.src = 'https://www.tiktok.com/embed.js';
    tiktokScript.async = true;

    document.body.appendChild(instagramScript);
    document.body.appendChild(tiktokScript);

    return () => {
      document.body.removeChild(instagramScript);
      document.body.removeChild(tiktokScript);
    };
  }, []);

  return null;
};

export default ContentClientEnhancer;
